import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/api';
import type { VersionHistoryResponse, VersionEntry, VersionChange } from '../../types';

const categoryConfig: Record<VersionChange['category'], { label: string; bg: string; text: string }> = {
  feature: { label: 'Feature', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  fix: { label: 'Bug Fix', bg: 'bg-red-500/20', text: 'text-red-400' },
  improvement: { label: 'Improvement', bg: 'bg-green-500/20', text: 'text-green-400' },
  breaking: { label: 'Breaking', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  security: { label: 'Security', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  documentation: { label: 'Documentation', bg: 'bg-slate-600/40', text: 'text-slate-300' },
  test: { label: 'Tests', bg: 'bg-teal-500/20', text: 'text-teal-400' },
  removed: { label: 'Removed', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  note: { label: 'Note', bg: 'bg-slate-700/60', text: 'text-slate-300' },
};

function formatReleaseDate(releasedAt: string): string {
  try {
    return new Date(releasedAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return releasedAt;
  }
}

function groupChangesByCategory(changes: VersionChange[]): Record<VersionChange['category'], VersionChange[]> {
  const grouped: Record<VersionChange['category'], VersionChange[]> = {
    feature: [],
    fix: [],
    improvement: [],
    breaking: [],
    security: [],
    documentation: [],
    test: [],
    removed: [],
    note: [],
  };

  for (const change of changes) {
    const bucket = Object.hasOwn(grouped, change.category) ? grouped[change.category] : grouped.note;
    bucket.push(change);
  }

  return grouped;
}

interface VersionCardProps {
  version: VersionEntry;
  isLast: boolean;
}

function VersionCard({ version, isLast }: VersionCardProps) {
  const groupedChanges = useMemo(() => groupChangesByCategory(version.changes), [version.changes]);

  return (
    <div className="relative pl-8">
      <div className="absolute left-2 top-7 h-3 w-3 rounded-full bg-slate-300 dark:bg-slate-500" />
      {!isLast && <div className="absolute left-[13px] top-10 h-[calc(100%-1.5rem)] w-px bg-slate-700" />}

      <article className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-md bg-slate-700 border border-slate-600 px-2.5 py-1 text-xs font-mono text-slate-200">
              v{version.version}
            </span>
            <span className="text-sm text-slate-400">{formatReleaseDate(version.releasedAt)}</span>
          </div>
        </div>

        <p className="text-slate-200">{version.summary}</p>

        <div className="space-y-3">
          {(Object.keys(categoryConfig) as VersionChange['category'][]).map((category) => {
            const items = groupedChanges[category];
            if (!items || items.length === 0) {
              return null;
            }

            const cfg = categoryConfig[category];
            return (
              <section key={category} className="space-y-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`}
                >
                  {cfg.label}
                </span>
                <ul className="space-y-1">
                  {items.map((change, index) => (
                    <li key={`${category}-${index}`} className="text-sm text-slate-300 flex items-start">
                      <span className="mr-2 mt-1 text-slate-500">•</span>
                      <span>{change.description}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </article>
    </div>
  );
}

export function VersionHistoryTab() {
  const [history, setHistory] = useState<VersionHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadVersionHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiClient.getVersionHistory();
        setHistory(data);
      } catch (err: unknown) {
        const apiError = err as { response?: { data?: { title?: string } } };
        setError(apiError.response?.data?.title || 'Failed to load version history');
      } finally {
        setLoading(false);
      }
    };

    loadVersionHistory();
  }, []);

  if (loading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <p className="text-slate-300">Loading version history…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
        <h3 className="text-red-300 font-semibold mb-2">Error</h3>
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!history) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <p className="text-slate-300">No version history available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
        <p className="text-sm text-slate-400 mb-2">Current Version</p>
        <div className="inline-flex items-center rounded-md bg-emerald-500/20 border border-emerald-500/40 px-3 py-1.5 text-sm font-semibold text-emerald-300">
          v{history.currentVersion}
        </div>
      </div>

      <div className="space-y-4">
        {history.versions.length === 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <p className="text-slate-300">No released versions found.</p>
          </div>
        ) : (
          history.versions.map((version, index) => (
            <VersionCard
              key={`${version.version}-${version.releasedAt}`}
              version={version}
              isLast={index === history.versions.length - 1}
            />
          ))
        )}
      </div>
    </div>
  );
}
